import React from "react";
import ProtectedRoute from "../components/common/ProtectedRoute";

// Lazy load admin layout and dashboard components
const Dashboard = React.lazy(() => import("../pages/dashboard/Dashboard.jsx"));
const AdminLayout = React.lazy(() =>
    import("@/components/layouts/AdminLayout")
);

const PaymentSuccessPage = React.lazy(() =>
    import("../pages/dashboard/payments/PaymentSuccessPage")
);
const PaymentCallBackReceiver = React.lazy(() =>
    import("../pages/PaymentCallBackReceiver")
);
// POLICIES PAGES
const PoliciesPage = React.lazy(() =>
    import("../pages/dashboard/policies/PoliciesPage")
);
const PolicyDetailsPage = React.lazy(() =>
    import("../pages/dashboard/policies/PolicyDetailsPage")
);
const EditPolicyFreqPage = React.lazy(() =>
    import("../pages/dashboard/policies/EditPolicyFreqPage")
);
const CancelPolicyPage = React.lazy(() =>
    import("../pages/dashboard/policies/CancelPolicyPage")
);
const AddRidersPage = React.lazy(() =>
    import("../pages/dashboard/policies/AddRidersPage")
);
const BeneficiaryDetailsPage = React.lazy(() =>
    import("../pages/dashboard/policies/BeneficiaryDetailsPage")
);
const EditBeneficiaryPage = React.lazy(() =>
    import("../pages/dashboard/policies/EditBeneficiaryPage")
);
const ManageAllocationPage = React.lazy(() =>
    import("../pages/dashboard/policies/ManageAllocationPage")
);

// SAVINGS PAGES
const SavingsPage = React.lazy(() =>
    import("../pages/dashboard/savings/SavingsPage.jsx")
);
const SavingsDetailsPage = React.lazy(() =>
    import("../pages/dashboard/savings/SavingsDetailsPage.jsx")
);
const SavingsFundsPage = React.lazy(() =>
    import("../pages/dashboard/savings/SavingsFundsPage.jsx")
);
const SavingsPaymentSummaryPage = React.lazy(() =>
    import("../pages/dashboard/savings/SavingsPaymentSummaryPage.jsx")
);
const SavingsPaymentSelectionPage = React.lazy(() =>
    import("../pages/dashboard/savings/SavingsPaymentSelectionPage.jsx")
);
// OTHERS PAGES
const ClaimsPage = React.lazy(() =>
    import("../pages/dashboard/claims/ClaimsPage")
);
const AvailableClaimsPage = React.lazy(() =>
    import("../pages/dashboard/claims/AvailableClaimsPage")
);
const FillingClaimParent = React.lazy(() =>
    import("../pages/dashboard/claims/FillingClaimParent")
);
const TrackDetailedPage = React.lazy(() =>
    import("../pages/dashboard/claims/TrackDetailedPage")
);


const PaymentsPage = React.lazy(() =>
    import("../pages/dashboard/sections/PaymentsPage")
);
const RewardsPage = React.lazy(() =>
    import("../pages/dashboard/rewards/RewardsPage")
);
// Old Learn & Earn module — temporarily disabled, replaced by the Financial Literacy
// Journey below (now reusing the "Learn & Earn" name/route). Keeping this commented
// out (not deleted) so it can be restored easily if needed.
// const LearnAndEarnPage = React.lazy(() =>
//     import("../pages/dashboard/rewards/LearnAndEarnPage")
// );
const QuestsHubPage = React.lazy(() =>
    import("../pages/dashboard/rewards/financialLiteracy/QuestsHubPage")
);
const QuestDetailPage = React.lazy(() =>
    import("../pages/dashboard/rewards/financialLiteracy/QuestDetailPage")
);
const LessonPage = React.lazy(() =>
    import("../pages/dashboard/rewards/financialLiteracy/LessonPage")
);
const GiveBackPage = React.lazy(() =>
    import("../pages/dashboard/giveBack/GiveBackPage")
);
const InviteFriendsPage = React.lazy(() =>
    import("../pages/dashboard/inviteFriends/InviteFriendsPage")
);
const ServicesPage = React.lazy(() =>
    import("../pages/dashboard/services/ServicesPage")
);
const DigitalBusinessCardPage = React.lazy(() =>
    import("../pages/dashboard/businessCard/DigitalBusinessCardPage")
);
const RequestsPage = React.lazy(() =>
    import("../pages/dashboard/requests/RequestPage")
);
const HelpPage = React.lazy(() =>
    import("../pages/dashboard/helpCenter/HelpCenterPage")
);
const SettingsPage = React.lazy(() =>
    import("../pages/dashboard/settings/SettingsPage")
);
const ActivitiesPage = React.lazy(() =>
    import("../pages/dashboard/activities/ActivitiesPage")
);
const NotificationsPage = React.lazy(() =>
    import("../pages/dashboard/notifications/NotificationsPage")
);
const PoliciesWhatsCoveredPage = React.lazy(() =>
    import("../pages/dashboard/policies/PoliciesWhatsCoveredPage")
);
const DonateNowPage = React.lazy(() =>
    import("../pages/dashboard/donatenow/DonateNowPage")
);

// Helper function to wrap pages with AdminLayout and ProtectedRoute
const createProtectedAdminRoute = (PageComponent) => (
    <ProtectedRoute requireAuth={true} redirectTo="/login">
        <AdminLayout>
            <PageComponent />
        </AdminLayout>
    </ProtectedRoute>
);

// Export route configuration array
export const dashboardRoutes = [
    { path: "/admin", element: createProtectedAdminRoute(Dashboard) },
    // Policies routes
    { path: "/admin/policies", element: createProtectedAdminRoute(PoliciesPage) },
    { path: "/admin/policies/whats-covered", element: createProtectedAdminRoute(PoliciesWhatsCoveredPage) },
    { path: "/admin/policies/:policyId", element: createProtectedAdminRoute(PolicyDetailsPage) },
    { path: "/admin/policies/:policyId/add-riders", element: createProtectedAdminRoute(AddRidersPage) },
    { path: "/admin/policies/:policyId/edit/freq", element: createProtectedAdminRoute(EditPolicyFreqPage) },
    { path: "/admin/policies/:policyId/cancel", element: createProtectedAdminRoute(CancelPolicyPage) },
    { path: "/admin/policies/:policyId/cancel/payment-method-selection", element: createProtectedAdminRoute(SavingsPaymentSelectionPage) },

    // Policies/Beneficiraries routes
    { path: "/admin/policies/:policyId/beneficiaries/:beneficiaryId", element: createProtectedAdminRoute(BeneficiaryDetailsPage) },
    { path: "/admin/policies/:policyId/beneficiaries/:beneficiaryId/edit", element: createProtectedAdminRoute(EditBeneficiaryPage) },
    { path: "/admin/policies/:policyId/beneficiaries/new", element: createProtectedAdminRoute(EditBeneficiaryPage) },
    { path: "/admin/policies/:policyId/beneficiaries/manage-allocation", element: createProtectedAdminRoute(ManageAllocationPage) },

    // savings add funds payment summary and paying page
    { path: "/admin/policies/:savingId/payment", element: createProtectedAdminRoute(SavingsPaymentSummaryPage) },

    // *********************************************************** Savings routes ****************************************************************************
    { path: "/admin/savings", element: createProtectedAdminRoute(SavingsPage) },
    { path: "/admin/savings/whats-covered", element: createProtectedAdminRoute(PoliciesWhatsCoveredPage) },

    { path: "/admin/savings/:savingId", element: createProtectedAdminRoute(SavingsDetailsPage) },
    // Savings/Beneficiaries routes
    { path: "/admin/savings/:policyId/beneficiaries/new", element: createProtectedAdminRoute(EditBeneficiaryPage) },
    { path: "/admin/savings/:policyId/beneficiaries/manage-allocation", element: createProtectedAdminRoute(ManageAllocationPage) },
    { path: "/admin/savings/:policyId/beneficiaries/:beneficiaryId", element: createProtectedAdminRoute(BeneficiaryDetailsPage) },
    { path: "/admin/savings/:policyId/beneficiaries/:beneficiaryId/edit", element: createProtectedAdminRoute(EditBeneficiaryPage) },
    // Savimngs/Funds Routes
    { path: "/admin/savings/:savingId/funds/:actionType", element: createProtectedAdminRoute(SavingsFundsPage) },
    // savings add funds payment summary and paying page
    { path: "/admin/savings/:savingId/funds/add/payment", element: createProtectedAdminRoute(SavingsPaymentSummaryPage) },
    // For savings withdraw 
    { path: "/admin/savings/:savingId/funds/:actionType/payment-method-selection", element: createProtectedAdminRoute(SavingsPaymentSelectionPage) },
    { path: "/admin/savings/:savingId/funds/:actionType/success", element: createProtectedAdminRoute(PaymentSuccessPage) },
    // OTHERS
    { path: "/admin/claims", element: createProtectedAdminRoute(ClaimsPage) },
    { path: "/admin/available-claims", element: createProtectedAdminRoute(AvailableClaimsPage) },
    { path: "/admin/filling-claim", element: createProtectedAdminRoute(FillingClaimParent) },
    { path: "/admin/track-detail-claim", element: createProtectedAdminRoute(TrackDetailedPage) },
    { path: "/admin/payments", element: createProtectedAdminRoute(PaymentsPage) },
    { path: "/admin/rewards", element: createProtectedAdminRoute(RewardsPage) },
    // Old Learn & Earn module — commented out, not deleted (see lazy import above).
    // { path: "/admin/rewards/learn-earn", element: createProtectedAdminRoute(LearnAndEarnPage) },
    { path: "/admin/learn-earn", element: createProtectedAdminRoute(QuestsHubPage) },
    { path: "/admin/learn-earn/:questId", element: createProtectedAdminRoute(QuestDetailPage) },
    { path: "/admin/learn-earn/:questId/:missionId", element: createProtectedAdminRoute(LessonPage) },
    { path: "/admin/give-back", element: createProtectedAdminRoute(GiveBackPage) },
    { path: "/admin/invite-friends", element: createProtectedAdminRoute(InviteFriendsPage) },
    { path: "/admin/subscriptions", element: createProtectedAdminRoute(ServicesPage) },
    { path: "/admin/business-card", element: createProtectedAdminRoute(DigitalBusinessCardPage) },
    { path: "/admin/requests", element: createProtectedAdminRoute(RequestsPage) },
    { path: "/admin/help-center", element: createProtectedAdminRoute(HelpPage) },
    { path: "/admin/settings", element: createProtectedAdminRoute(SettingsPage) },

    // Other Than Menues
    { path: "/admin/activities", element: createProtectedAdminRoute(ActivitiesPage) },
    { path: "/admin/notifications", element: createProtectedAdminRoute(NotificationsPage) },
    { path: "/admin/donate-now", element: createProtectedAdminRoute(DonateNowPage) },

    //Payment Callback Receiver
    { path: "/admin/payment-callback", element: createProtectedAdminRoute(PaymentCallBackReceiver) },
];